import { BaseService } from '../base/base.service';
import { resolveRuntimeApiGatewayUrl } from '@/config/runtime-api';
import type {
  AllPermissionsResponse,
  Permission,
  PermissionGroup,
} from './role.types';

/**
 * Permission Service
 * Provides permission management functionality for admin panel
 * Integrates with ABP Permission Management APIs
 *
 * Features:
 * - Retrieve all available permissions
 * - Group permissions by provider/resource
 * - Permission definition queries
 *
 * API Endpoints:
 * - GET /api/v1/permission-management/permissions/all - Get all permissions
 *
 * Note: Permission assignment to roles is handled by RoleService
 */
export class PermissionService extends BaseService {
  private permissionsCache: AllPermissionsResponse | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor() {
    super('/api/permission-management');
  }

  /**
   * Get all available permissions in the system
   * Uses ABP's application-configuration endpoint which returns permission definitions
   * without requiring provider-specific parameters
   * Results are cached for 5 minutes to reduce API calls
   *
   * @param forceRefresh - Force refresh from API, bypassing cache
   * @returns All permissions with metadata
   *
   * @example
   * ```ts
   * const permissions = await permissionService.getAllPermissions();
   * console.log(`Found ${permissions.permissions.length} permissions`);
   * ```
   */
  async getAllPermissions(
    forceRefresh: boolean = false
  ): Promise<AllPermissionsResponse> {
    // Check cache validity
    if (
      !forceRefresh &&
      this.permissionsCache &&
      Date.now() - this.cacheTimestamp < this.CACHE_DURATION
    ) {
      return this.permissionsCache;
    }

    try {
      // Use ABP Permission Management API to get ALL permission definitions
      // This queries the permission definitions from all registered providers
      const baseUrl = resolveRuntimeApiGatewayUrl();
      
      interface PermissionGroup {
        name: string;
        displayName: string;
        permissions: Array<{
          name: string;
          displayName: string;
          parentName?: string;
          isGranted: boolean;
        }>;
      }

      interface PermissionResponse {
        entityDisplayName: string;
        groups: PermissionGroup[];
      }

      // Collect permissions from multiple sources
      const permissions: Array<{
        name: string;
        displayName: string;
        description?: string;
        providerName: string;
        parentName?: string;
        isEnabled: boolean;
      }> = [];

      // Identity service now emits permission definitions for all modules (identity, wallet, engagement, etc.)
      const { data: servicePerms } = await this.api.get<PermissionResponse>(
        `${baseUrl}/api/permission-management/permissions`,
        {
          params: {
            providerName: 'R',
            providerKey: 'admin'
          }
        }
      );

      servicePerms.groups.forEach((group) => {
        const groupProviderName = group.name.split('.')[0] || 'Identity';

        group.permissions.forEach((permission) => {
          permissions.push({
            name: permission.name,
            displayName: permission.displayName,
            description: '',
            providerName: groupProviderName,
            parentName: permission.parentName,
            isEnabled: true
          });
        });
      });

      const response: AllPermissionsResponse = { permissions };

      // Update cache
      this.permissionsCache = response;
      this.cacheTimestamp = Date.now();

      return response;
    } catch (error) {
      console.warn('Permission API not available, using mock data:', error);

      // Fallback: Return mock permissions for development
      const mockPermissions = this.getMockPermissions();

      this.permissionsCache = mockPermissions;
      this.cacheTimestamp = Date.now();

      return mockPermissions;
    }
  }

  /**
   * Get mock permissions for development/fallback
   * Returns a comprehensive set of permissions for the Tagna system
   */
  private getMockPermissions(): AllPermissionsResponse {
    const permissions = [
      // Identity Management
      { name: 'AbpIdentity.Roles', displayName: 'Role Management', description: 'Manage roles', providerName: 'Identity', parentName: null, isEnabled: true },
      { name: 'AbpIdentity.Roles.Create', displayName: 'Create Role', description: 'Create new roles', providerName: 'Identity', parentName: 'AbpIdentity.Roles', isEnabled: true },
      { name: 'AbpIdentity.Roles.Update', displayName: 'Update Role', description: 'Update existing roles', providerName: 'Identity', parentName: 'AbpIdentity.Roles', isEnabled: true },
      { name: 'AbpIdentity.Roles.Delete', displayName: 'Delete Role', description: 'Delete roles', providerName: 'Identity', parentName: 'AbpIdentity.Roles', isEnabled: true },
      { name: 'AbpIdentity.Users', displayName: 'User Management', description: 'Manage users', providerName: 'Identity', parentName: null, isEnabled: true },
      { name: 'AbpIdentity.Users.Create', displayName: 'Create User', description: 'Create new users', providerName: 'Identity', parentName: 'AbpIdentity.Users', isEnabled: true },
      { name: 'AbpIdentity.Users.Update', displayName: 'Update User', description: 'Update existing users', providerName: 'Identity', parentName: 'AbpIdentity.Users', isEnabled: true },
      { name: 'AbpIdentity.Users.Delete', displayName: 'Delete User', description: 'Delete users', providerName: 'Identity', parentName: 'AbpIdentity.Users', isEnabled: true },

      // User Service
      { name: 'User.Users', displayName: 'User Operations', description: 'Manage user operations', providerName: 'User', parentName: null, isEnabled: true },
      { name: 'User.Users.Create', displayName: 'Create User', description: 'Create new users', providerName: 'User', parentName: 'User.Users', isEnabled: true },
      { name: 'User.Users.Update', displayName: 'Update User', description: 'Update user details', providerName: 'User', parentName: 'User.Users', isEnabled: true },
      { name: 'User.Users.Delete', displayName: 'Delete User', description: 'Delete users', providerName: 'User', parentName: 'User.Users', isEnabled: true },

      // Gamification Service
      { name: 'Gamification.Achievements', displayName: 'Achievement Management', description: 'Manage achievements', providerName: 'Gamification', parentName: null, isEnabled: true },
      { name: 'Gamification.Achievements.Create', displayName: 'Create Achievement', description: 'Create new achievements', providerName: 'Gamification', parentName: 'Gamification.Achievements', isEnabled: true },
      { name: 'Gamification.Achievements.Update', displayName: 'Update Achievement', description: 'Update existing achievements', providerName: 'Gamification', parentName: 'Gamification.Achievements', isEnabled: true },
      { name: 'Gamification.Achievements.Delete', displayName: 'Delete Achievement', description: 'Delete achievements', providerName: 'Gamification', parentName: 'Gamification.Achievements', isEnabled: true },
      { name: 'Gamification.Leaderboards', displayName: 'Leaderboard Management', description: 'Manage leaderboards', providerName: 'Gamification', parentName: null, isEnabled: true },
      { name: 'Gamification.Leaderboards.Create', displayName: 'Create Leaderboard', description: 'Create new leaderboards', providerName: 'Gamification', parentName: 'Gamification.Leaderboards', isEnabled: true },
      { name: 'Gamification.Rewards', displayName: 'Reward Management', description: 'Manage rewards', providerName: 'Gamification', parentName: null, isEnabled: true },
      { name: 'Gamification.Rewards.Create', displayName: 'Create Reward', description: 'Create new rewards', providerName: 'Gamification', parentName: 'Gamification.Rewards', isEnabled: true },

      // Content Service
      { name: 'Content.Posts', displayName: 'Post Management', description: 'Manage posts', providerName: 'Content', parentName: null, isEnabled: true },
      { name: 'Content.Posts.Create', displayName: 'Create Post', description: 'Create new posts', providerName: 'Content', parentName: 'Content.Posts', isEnabled: true },
      { name: 'Content.Posts.Update', displayName: 'Update Post', description: 'Update existing posts', providerName: 'Content', parentName: 'Content.Posts', isEnabled: true },
      { name: 'Content.Posts.Delete', displayName: 'Delete Post', description: 'Delete posts', providerName: 'Content', parentName: 'Content.Posts', isEnabled: true },
      { name: 'Content.Announcements', displayName: 'Announcement Management', description: 'Manage announcements', providerName: 'Content', parentName: null, isEnabled: true },
      { name: 'Content.Announcements.Create', displayName: 'Create Announcement', description: 'Create new announcements', providerName: 'Content', parentName: 'Content.Announcements', isEnabled: true },

      // Analytics Service
      { name: 'Analytics.Reports', displayName: 'Report Management', description: 'Manage analytics reports', providerName: 'Analytics', parentName: null, isEnabled: true },
      { name: 'Analytics.Reports.View', displayName: 'View Reports', description: 'View analytics reports', providerName: 'Analytics', parentName: 'Analytics.Reports', isEnabled: true },
      { name: 'Analytics.Reports.Create', displayName: 'Create Report', description: 'Create custom reports', providerName: 'Analytics', parentName: 'Analytics.Reports', isEnabled: true },
      { name: 'Analytics.Reports.Export', displayName: 'Export Reports', description: 'Export analytics reports', providerName: 'Analytics', parentName: 'Analytics.Reports', isEnabled: true },
      { name: 'Analytics.Dashboard', displayName: 'Dashboard Access', description: 'Access analytics dashboards', providerName: 'Analytics', parentName: null, isEnabled: true },

      // Wallet Service
      { name: 'Wallet.Transactions', displayName: 'Transaction Management', description: 'Manage wallet transactions', providerName: 'Wallet', parentName: null, isEnabled: true },
      { name: 'Wallet.Transactions.Create', displayName: 'Create Transaction', description: 'Create new transactions', providerName: 'Wallet', parentName: 'Wallet.Transactions', isEnabled: true },
      { name: 'Wallet.Transactions.View', displayName: 'View Transactions', description: 'View transaction history', providerName: 'Wallet', parentName: 'Wallet.Transactions', isEnabled: true },
      { name: 'Wallet.Balance', displayName: 'Balance Management', description: 'Manage user balances', providerName: 'Wallet', parentName: null, isEnabled: true },
      { name: 'Wallet.Balance.Adjust', displayName: 'Adjust Balance', description: 'Manually adjust user balances', providerName: 'Wallet', parentName: 'Wallet.Balance', isEnabled: true },

      // Communication Service
      { name: 'Communication.Notifications', displayName: 'Notification Management', description: 'Manage notifications', providerName: 'Communication', parentName: null, isEnabled: true },
      { name: 'Communication.Notifications.Send', displayName: 'Send Notification', description: 'Send notifications to users', providerName: 'Communication', parentName: 'Communication.Notifications', isEnabled: true },
      { name: 'Communication.Notifications.View', displayName: 'View Notifications', description: 'View notification history', providerName: 'Communication', parentName: 'Communication.Notifications', isEnabled: true },
      { name: 'Communication.Messages', displayName: 'Message Management', description: 'Manage user messages', providerName: 'Communication', parentName: null, isEnabled: true },

      // Admin Service
      { name: 'Admin.Settings', displayName: 'System Settings', description: 'Manage system settings', providerName: 'Admin', parentName: null, isEnabled: true },
      { name: 'Admin.Settings.Update', displayName: 'Update Settings', description: 'Update system settings', providerName: 'Admin', parentName: 'Admin.Settings', isEnabled: true },
      { name: 'Admin.FeatureFlags', displayName: 'Feature Flag Management', description: 'Manage feature flags', providerName: 'Admin', parentName: null, isEnabled: true },
      { name: 'Admin.FeatureFlags.Toggle', displayName: 'Toggle Features', description: 'Enable/disable features', providerName: 'Admin', parentName: 'Admin.FeatureFlags', isEnabled: true },
    ];

    return { permissions };
  }

  /**
   * Get permissions grouped by provider/resource
   * Useful for displaying permissions in a tree structure
   *
   * @param forceRefresh - Force refresh from API
   * @returns Map of provider name to permissions
   *
   * @example
   * ```ts
   * const grouped = await permissionService.getPermissionsByProvider();
   * // { 'Identity': [...], 'User': [...], 'Content': [...] }
   * ```
   */
  async getPermissionsByProvider(
    forceRefresh: boolean = false
  ): Promise<Map<string, Permission[]>> {
    const allPermissions = await this.getAllPermissions(forceRefresh);

    const grouped = new Map<string, Permission[]>();

    allPermissions.permissions.forEach((permission) => {
      const provider = permission.providerName || 'Other';

      if (!grouped.has(provider)) {
        grouped.set(provider, []);
      }

      grouped.get(provider)!.push({
        name: permission.name,
        displayName: permission.displayName,
        description: permission.description,
        parentName: permission.parentName,
        isGranted: false, // Default - actual grant status comes from role permissions
        allowedProviders: [], // Not available in all permissions response
      });
    });

    return grouped;
  }

  /**
   * Get permissions as groups (similar to ABP's permission group structure)
   * Converts flat permission list into hierarchical groups
   *
   * @param forceRefresh - Force refresh from API
   * @returns Array of permission groups
   *
   * @example
   * ```ts
   * const groups = await permissionService.getPermissionGroups();
   * groups.forEach(group => {
   *   console.log(`${group.displayName}: ${group.permissions.length} permissions`);
   * });
   * ```
   */
  async getPermissionGroups(
    forceRefresh: boolean = false
  ): Promise<PermissionGroup[]> {
    const byProvider = await this.getPermissionsByProvider(forceRefresh);

    const groups: PermissionGroup[] = [];

    byProvider.forEach((permissions, providerName) => {
      groups.push({
        name: providerName,
        displayName: this.formatProviderDisplayName(providerName),
        permissions,
      });
    });

    // Sort groups alphabetically
    return groups.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  /**
   * Search permissions by name or description
   *
   * @param query - Search query
   * @param forceRefresh - Force refresh from API
   * @returns Filtered permissions
   *
   * @example
   * ```ts
   * const userPermissions = await permissionService.searchPermissions('user');
   * ```
   */
  async searchPermissions(
    query: string,
    forceRefresh: boolean = false
  ): Promise<Permission[]> {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const allPermissions = await this.getAllPermissions(forceRefresh);
    const searchTerm = query.toLowerCase();

    return allPermissions.permissions
      .filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm) ||
          p.displayName.toLowerCase().includes(searchTerm) ||
          p.description?.toLowerCase().includes(searchTerm)
      )
      .map((p) => ({
        name: p.name,
        displayName: p.displayName,
        description: p.description,
        parentName: p.parentName,
        isGranted: false,
        allowedProviders: [],
      }));
  }

  /**
   * Get permissions for a specific provider/resource
   *
   * @param providerName - Provider name (e.g., 'Identity', 'User', 'Content')
   * @param forceRefresh - Force refresh from API
   * @returns Permissions for the provider
   *
   * @example
   * ```ts
   * const identityPerms = await permissionService.getProviderPermissions('Identity');
   * ```
   */
  async getProviderPermissions(
    providerName: string,
    forceRefresh: boolean = false
  ): Promise<Permission[]> {
    const byProvider = await this.getPermissionsByProvider(forceRefresh);
    return byProvider.get(providerName) || [];
  }

  /**
   * Get permission definition by name
   *
   * @param permissionName - Full permission name (e.g., 'Identity.Users.Create')
   * @param forceRefresh - Force refresh from API
   * @returns Permission definition or null if not found
   *
   * @example
   * ```ts
   * const perm = await permissionService.getPermissionByName('Identity.Users.Create');
   * ```
   */
  async getPermissionByName(
    permissionName: string,
    forceRefresh: boolean = false
  ): Promise<Permission | null> {
    const allPermissions = await this.getAllPermissions(forceRefresh);

    const found = allPermissions.permissions.find(
      (p) => p.name === permissionName
    );

    if (!found) {
      return null;
    }

    return {
      name: found.name,
      displayName: found.displayName,
      description: found.description,
      parentName: found.parentName,
      isGranted: false,
      allowedProviders: [],
    };
  }

  /**
   * Get hierarchical permissions (parent-child relationships)
   * Builds a tree structure based on parentName relationships
   *
   * @param forceRefresh - Force refresh from API
   * @returns Map of parent permission names to their children
   *
   * @example
   * ```ts
   * const tree = await permissionService.getPermissionHierarchy();
   * const identityChildren = tree.get('Identity') || [];
   * ```
   */
  async getPermissionHierarchy(
    forceRefresh: boolean = false
  ): Promise<Map<string, Permission[]>> {
    const allPermissions = await this.getAllPermissions(forceRefresh);

    const hierarchy = new Map<string, Permission[]>();

    allPermissions.permissions.forEach((perm) => {
      const parentKey = perm.parentName || '__root__';

      if (!hierarchy.has(parentKey)) {
        hierarchy.set(parentKey, []);
      }

      hierarchy.get(parentKey)!.push({
        name: perm.name,
        displayName: perm.displayName,
        description: perm.description,
        parentName: perm.parentName,
        isGranted: false,
        allowedProviders: [],
      });
    });

    return hierarchy;
  }

  /**
   * Clear the permissions cache
   * Useful when permissions are added/removed dynamically
   *
   * @example
   * ```ts
   * permissionService.clearCache();
   * const fresh = await permissionService.getAllPermissions();
   * ```
   */
  clearCache(): void {
    this.permissionsCache = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Format provider name to display name
   * Converts technical names to human-readable format
   *
   * @example
   * 'Identity' -> 'Identity Management'
   * 'User' -> 'User Management'
   */
  private formatProviderDisplayName(providerName: string): string {
    const displayNames: Record<string, string> = {
      Identity: 'Identity Management',
      User: 'User Management',
      Gamification: 'Gamification & Engagement',
      Content: 'Content Management',
      Notifications: 'Notifications',
      Analytics: 'Analytics & Reporting',
      Admin: 'Admin Operations',
      Wallet: 'Wallet & Coins',
      Communication: 'Communication',
    };

    return displayNames[providerName] || `${providerName} Management`;
  }

  /**
   * Get permission statistics
   * Returns counts and metadata about permissions
   *
   * @param forceRefresh - Force refresh from API
   * @returns Permission statistics
   *
   * @example
   * ```ts
   * const stats = await permissionService.getPermissionStatistics();
   * console.log(`Total: ${stats.total}, Providers: ${stats.providerCount}`);
   * ```
   */
  async getPermissionStatistics(
    forceRefresh: boolean = false
  ): Promise<{
    total: number;
    providerCount: number;
    providers: { name: string; count: number }[];
  }> {
    const byProvider = await this.getPermissionsByProvider(forceRefresh);

    const providers = Array.from(byProvider.entries()).map(([name, perms]) => ({
      name,
      count: perms.length,
    }));

    return {
      total: Array.from(byProvider.values()).reduce(
        (sum, perms) => sum + perms.length,
        0
      ),
      providerCount: byProvider.size,
      providers: providers.sort((a, b) => b.count - a.count),
    };
  }
}

// Export singleton instance
export const permissionService = new PermissionService();
